import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Camera,
  Check,
  Clock,
  KeyRound,
  Loader2,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";

const MAX_AVATAR_BYTES = 500 * 1024; // 500KB pre-base64
const initialsOf = (s) => (s || "?").split(/[\s@.]/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
const formatStamp = (iso) => formatDateTime(iso);

const Section = ({ eyebrow, title, description, children, testId }) => (
  <section className="surface-card" data-testid={testId}>
    <div className="px-5 sm:px-6 py-5 border-b border-[#F1F5F9]">
      {eyebrow && <p className="text-xs font-medium text-[#3B82F6]">{eyebrow}</p>}
      <h2 className="font-['Geist'] font-semibold text-lg tracking-tight mt-0.5 text-[#0F172A]">{title}</h2>
      {description && <p className="text-sm text-[#64748B] mt-1.5">{description}</p>}
    </div>
    <div className="px-5 sm:px-6 py-5">{children}</div>
  </section>
);

export default function ProfilePage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Profile section state
  const [name, setName] = useState(user?.name || "");
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null);
  const [avatarSubmitting, setAvatarSubmitting] = useState(false);
  const [savingName, setSavingName] = useState(false);

  // Password change state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // Delete-account dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const dirtyName = (user?.name || "") !== name.trim();
  const dirtyAvatar = (user?.avatar_url || null) !== avatarPreview;
  const pwRulesOk = newPw.length >= 8 && /[A-Za-z]/.test(newPw) && /[0-9]/.test(newPw);
  const pwMatch = newPw === confirmPw && newPw.length > 0;

  const handlePickFile = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, WebP)");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error(`Image is too large. Max ${Math.round(MAX_AVATAR_BYTES / 1024)}KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.onerror = () => toast.error("Could not read the selected file");
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    const payload = {};
    if (dirtyName) payload.name = name.trim();
    if (dirtyAvatar) payload.avatar_url = avatarPreview || "";
    if (Object.keys(payload).length === 0) return;
    setSavingName(true);
    setAvatarSubmitting(true);
    try {
      await api.patch("/auth/me", payload);
      await refreshUser();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingName(false);
      setAvatarSubmitting(false);
    }
  };

  const handleResetAvatar = () => setAvatarPreview(null);

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();
    if (!pwRulesOk || !pwMatch) return;
    setPwSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated. You'll stay signed in on this device.");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) return;
    setDeleteSubmitting(true);
    try {
      await api.delete("/auth/me", { data: { password: deletePassword } });
      toast.success("Your account has been deleted");
      setDeleteOpen(false);
      setDeletePassword("");
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const isSuperAdmin = user?.global_role === "super_admin";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10 space-y-6" data-testid="profile-page">
      <div>
        <p className="label-caps">Your account</p>
        <h1 className="font-['Geist'] font-semibold text-2xl sm:text-3xl tracking-tight mt-1">Profile</h1>
        <p className="text-sm text-[#6B7280] mt-2 max-w-xl">
          Personal details, sign-in credentials and account controls. Your email is used as your sign-in identity and can&apos;t be changed here.
        </p>
      </div>

      {/* ----- Profile (avatar + name + email) ----- */}
      <Section
        eyebrow="Identity"
        title="Profile details"
        description="How your name and avatar appear in Probe — to teammates and on shared workspaces."
        testId="profile-details-section"
      >
        <div className="flex items-start gap-5 sm:gap-7 flex-col sm:flex-row">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative w-24 h-24 border border-[#D1D5DB] bg-white dark:bg-[#0F172A] dark:border-[#334155] flex items-center justify-center overflow-hidden">
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                  data-testid="profile-avatar-preview"
                />
              ) : (
                <span className="font-['Geist'] font-semibold text-2xl tracking-wider text-black dark:text-white">
                  {initialsOf(user?.email)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handlePickFile}
                data-testid="profile-avatar-upload-button"
                className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-medium rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC]"
              >
                <Camera size={11} />
                Change
              </button>
              {avatarPreview && (
                <button
                  type="button"
                  onClick={handleResetAvatar}
                  data-testid="profile-avatar-clear-button"
                  className="inline-flex items-center gap-1 px-2.5 h-8 text-xs font-medium rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC]"
                  aria-label="Remove avatar"
                >
                  <X size={11} />
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#94A3B8]">PNG · JPG · WebP · ≤500KB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={handleFileChange}
              data-testid="profile-avatar-file-input"
            />
          </div>

          {/* Fields */}
          <div className="flex-1 w-full space-y-4">
            <div>
              <label htmlFor="profile-name" className="text-sm font-medium text-[#475569]">Full name</label>
              <Input
                id="profile-name"
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How you'd like to appear"
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6] mt-1.5"
                maxLength={120}
              />
            </div>
            <div>
              <label htmlFor="profile-email" className="text-sm font-medium text-[#475569]">Email</label>
              <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md border border-[#E5E7EB] bg-[#F8FAFC] px-3 h-10">
                <span data-testid="profile-email-display" className="text-sm text-[#0F172A] font-mono truncate">
                  {user?.email}
                </span>
                <span className="text-[11px] font-medium text-[#94A3B8] shrink-0">
                  Read only
                </span>
              </div>
            </div>
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={(!dirtyName && !dirtyAvatar) || savingName || avatarSubmitting}
                data-testid="profile-save-button"
                className="inline-flex items-center gap-1.5 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium disabled:opacity-50 hover:bg-[#2563EB]"
              >
                {savingName || avatarSubmitting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                Save changes
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* ----- Password change ----- */}
      <Section
        eyebrow="Security"
        title="Change password"
        description="Min 8 characters with at least one letter and one number. You'll stay signed in on this device after the change."
        testId="profile-password-section"
      >
        <form onSubmit={handleChangePassword} className="grid grid-cols-1 gap-4 max-w-md">
          <div>
            <label htmlFor="current-pw" className="label-caps">Current password</label>
            <Input
              id="current-pw"
              data-testid="profile-current-password-input"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              autoComplete="current-password"
              className="rounded-none border-[#D1D5DB] focus-visible:ring-[#000000] mt-1.5"
            />
          </div>
          <div>
            <label htmlFor="new-pw" className="label-caps">New password</label>
            <Input
              id="new-pw"
              data-testid="profile-new-password-input"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              className="rounded-none border-[#D1D5DB] focus-visible:ring-[#000000] mt-1.5"
            />
            {newPw && !pwRulesOk && (
              <p className="text-[11px] text-[#374151] mt-1.5">
                Use at least 8 characters with one letter and one number.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="confirm-pw" className="label-caps">Confirm new password</label>
            <Input
              id="confirm-pw"
              data-testid="profile-confirm-password-input"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              autoComplete="new-password"
              className="rounded-none border-[#D1D5DB] focus-visible:ring-[#000000] mt-1.5"
            />
            {confirmPw && newPw !== confirmPw && (
              <p className="text-[11px] text-[#000000] mt-1.5">Passwords don&apos;t match.</p>
            )}
          </div>
          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={pwSubmitting || !currentPw || !pwRulesOk || !pwMatch}
              data-testid="profile-change-password-button"
              className="inline-flex items-center gap-1.5 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium disabled:opacity-50 hover:bg-[#2563EB]"
            >
              {pwSubmitting ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
              Update password
            </button>
          </div>
        </form>
      </Section>

      {/* ----- Account meta ----- */}
      <Section eyebrow="Account" title="Account information" testId="profile-account-info-section">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="label-caps mb-1">Member since</dt>
            <dd className="font-mono text-[13px] text-black dark:text-white" data-testid="profile-created-at">
              {formatStamp(user?.created_at)}
            </dd>
          </div>
          <div>
            <dt className="label-caps mb-1">Last sign-in</dt>
            <dd className="font-mono text-[13px] text-black dark:text-white flex items-center gap-1.5" data-testid="profile-last-login">
              <Clock size={12} className="text-[#6B7280]" />
              {formatStamp(user?.last_login_at)}
            </dd>
          </div>
          <div>
            <dt className="label-caps mb-1">Role</dt>
            <dd
              data-testid="profile-role"
              className={`inline-block font-mono text-[10px] uppercase tracking-wider border px-2 py-0.5 ${
                isSuperAdmin ? "bg-black text-white border-black" : "bg-white text-black border-[#D1D5DB] dark:bg-white/10 dark:text-white dark:border-white/20"
              }`}
            >
              {user?.global_role || "customer"}
            </dd>
          </div>
          <div>
            <dt className="label-caps mb-1">Account status</dt>
            <dd
              data-testid="profile-status"
              className="inline-block font-mono text-[10px] uppercase tracking-wider border border-black bg-black text-white px-2 py-0.5"
            >
              {user?.status || "active"}
            </dd>
          </div>
        </dl>
      </Section>

      {/* ----- Danger zone ----- */}
      {!isSuperAdmin && (
        <section
          className="border border-[#B91C1C] bg-white"
          data-testid="profile-danger-zone"
        >
          <div className="px-5 sm:px-6 py-4 border-b border-[#B91C1C] flex items-center gap-2">
            <AlertTriangle size={14} className="text-[#B91C1C]" />
            <p className="label-caps text-[#B91C1C]">Danger zone</p>
          </div>
          <div className="px-5 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="max-w-md">
              <h3 className="font-['Geist'] font-semibold text-lg tracking-tight">Delete this account</h3>
              <p className="text-sm text-[#374151] mt-1.5">
                Once deleted, you'll be signed out immediately and can't sign back in. Your owned data stays in
                the system for audit purposes but is no longer accessible to you.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              data-testid="profile-delete-open-button"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold tracking-[0.12em] border border-[#B91C1C] bg-white text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white transition-colors shrink-0"
            >
              <Trash2 size={12} />
              DELETE MY ACCOUNT
            </button>
          </div>
        </section>
      )}

      {/* Branded delete confirmation dialog */}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && !deleteSubmitting) {
            setDeleteOpen(false);
            setDeletePassword("");
          }
        }}
      >
        <AlertDialogContent
          data-testid="profile-delete-dialog"
          className="rounded-none border border-black bg-white p-0 max-w-md gap-0 shadow-[6px_6px_0_0_#000000]"
        >
          <AlertDialogHeader className="px-6 pt-6 pb-3 space-y-3">
            <div className="flex items-center justify-center w-11 h-11 bg-black text-white">
              <AlertTriangle size={18} />
            </div>
            <p className="font-mono text-[10px] tracking-[0.22em] text-[#6B7280] uppercase">
              Danger zone · Permanent
            </p>
            <AlertDialogTitle className="font-['Geist'] font-semibold tracking-tight text-2xl leading-tight">
              Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#374151] leading-relaxed pt-1">
              This will sign you out and lock{" "}
              <span className="font-mono text-[12px] text-black bg-[#F3F4F6] px-1.5 py-0.5 break-all">
                {user?.email}
              </span>{" "}
              from signing in again. Workspaces you own remain in the system as part of the audit trail. You won&apos;t be able to undo this yourself.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="px-6 pb-2 pt-2 space-y-1.5">
            <label className="label-caps" htmlFor="delete-confirm-pw">
              Confirm with your password <span className="text-black">*</span>
            </label>
            <Input
              id="delete-confirm-pw"
              data-testid="profile-delete-password-input"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Your current password"
              className="rounded-none border-[#D1D5DB] focus-visible:ring-[#000000]"
              autoComplete="current-password"
            />
          </div>

          <AlertDialogFooter className="px-6 pb-6 pt-3 flex-row justify-end gap-2 sm:gap-2">
            <AlertDialogCancel
              disabled={deleteSubmitting}
              data-testid="profile-delete-cancel"
              className="rounded-none border border-[#D1D5DB] bg-white text-black text-xs font-semibold tracking-[0.12em] h-10 px-4 mt-0 hover:bg-[#F3F4F6] hover:text-black"
            >
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              disabled={deleteSubmitting || !deletePassword}
              data-testid="profile-delete-confirm"
              className="rounded-none bg-black text-white text-xs font-semibold tracking-[0.12em] h-10 px-4 hover:bg-[#1F1F1F] inline-flex items-center gap-1.5"
            >
              {deleteSubmitting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              DELETE ACCOUNT
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
